/**
 * SparkMoment — app-wide "You created a SPARK" celebration.
 * Listens for window CustomEvent "resmo:spark" ({ detail: { kind } }) fired
 * after: tailored analysis completes, mock interview finishes, application sent.
 */
import React, { useEffect, useRef, useState } from "react";
import { Flame, X } from "lucide-react";
import { api } from "../lib/api";

const COPY = {
  tailor: {
    title: "Time to create a SPARK.",
    sub: "Your resume is tailored and ready — send it out into the world.",
  },
  interview: {
    title: "You created a SPARK today.",
    sub: "Every practice rep builds momentum toward the real thing.",
  },
  apply: {
    title: "Congratulations! You created another SPARK.",
    sub: "One more step toward your next opportunity.",
  },
};

export default function SparkMoment() {
  const [moment, setMoment] = useState(null);
  const [count, setCount] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onSpark = (e) => {
      const kind = e?.detail?.kind;
      if (!COPY[kind]) return;
      setCount(null);
      setMoment(kind);
      api.get("/sparks").then((r) => setCount(r.data?.total ?? null)).catch(() => {});
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setMoment(null), 7000);
    };
    window.addEventListener("resmo:spark", onSpark);
    return () => {
      window.removeEventListener("resmo:spark", onSpark);
      clearTimeout(timerRef.current);
    };
  }, []);

  if (!moment) return null;
  const copy = COPY[moment];

  return (
    <div className="fixed inset-x-0 bottom-8 z-[95] flex justify-center pointer-events-none px-4"
         data-testid="spark-moment">
      <style>{`
        @keyframes sparkRise { from { transform: translateY(28px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes sparkFlicker { 0%,100% { transform: scale(1); } 50% { transform: scale(1.25); } }
      `}</style>
      <div className="pointer-events-auto flex items-center gap-4 px-6 py-4 border-[3px]"
           style={{
             background: "#0F1B2D",
             borderColor: "#FF6A00",
             boxShadow: "6px 6px 0 0 rgba(255,106,0,0.35)",
             animation: "sparkRise .45s cubic-bezier(.2,.9,.3,1.2)",
             maxWidth: 480,
           }}>
        <span className="flex items-center justify-center w-11 h-11 rounded-full flex-shrink-0"
              style={{ background: "rgba(255,106,0,0.15)" }}>
          <Flame size={22} style={{ color: "#FF6A00", animation: "sparkFlicker 1.2s ease-in-out infinite" }} />
        </span>
        <div className="min-w-0">
          <div className="font-black text-white text-sm" data-testid="spark-moment-title">{copy.title}</div>
          <div className="text-xs mt-0.5" style={{ color: "#94A3B8" }}>{copy.sub}</div>
          {count != null && (
            <div className="inline-flex items-center gap-1 mt-1.5 font-mono-rm text-[10px] uppercase tracking-[2px] font-bold"
                 style={{ color: "#FFC145" }} data-testid="spark-moment-count">
              <Flame size={10} /> SPARK #{count}
            </div>
          )}
        </div>
        <button onClick={() => setMoment(null)} className="flex-shrink-0 self-start -mr-1"
                style={{ color: "#64748B" }} data-testid="spark-moment-dismiss">
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
